import { Alert, Button, Card, Empty, Input, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { SearchOutlined } from '@ant-design/icons'
import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { formatUtcTimeToLocalDisplay } from '../../features/reports/schedule/time'
import { useReportBuilderScheduleList } from '../../hooks/useReports'
import { useAuthSession } from '../../lib/session'
import type { ReportBuilderScheduleListItem } from '../../types/reports'

export default function ReportSchedulesPage() {
  const { reportBuilderId = '' } = useParams<{ reportBuilderId: string }>()
  const session = useAuthSession()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const clientId = session?.currentClientId || ''
  const [search, setSearch] = useState('')
  const isGroup = searchParams.get('type') === 'group'

  const schedulesQuery = useReportBuilderScheduleList(clientId, reportBuilderId, isGroup)

  const filteredRows = useMemo(() => {
    const rows = schedulesQuery.data || []
    const query = search.trim().toLowerCase()
    if (!query) return rows
    return rows.filter((row) => {
      const title = String(row.title || '').toLowerCase()
      const schedule = String(row.schedule || '').toLowerCase()
      const companyName = String(row.companyName || '').toLowerCase()
      return title.includes(query) || schedule.includes(query) || companyName.includes(query)
    })
  }, [schedulesQuery.data, search])

  const columns = useMemo<ColumnsType<ReportBuilderScheduleListItem>>(
    () => [
      {
        title: 'Report',
        dataIndex: 'title',
        key: 'title',
        render: (value, row) => (
          <Button
            type="link"
            className="px-0"
            onClick={() =>
              navigate(
                row.reportBuilderId && row.reportBuilderId !== '00000000-0000-0000-0000-000000000000'
                  ? `/reports/custom/${row.reportBuilderId}`
                  : `/reports/custom/${row.reportBuilderGroupId}?type=group`
              )
            }
          >
            {value || 'Report'}
          </Button>
        ),
      },
      {
        title: 'Schedule',
        dataIndex: 'schedule',
        key: 'schedule',
      },
      {
        title: 'Time',
        dataIndex: 'time',
        key: 'time',
        render: (value) => formatUtcTimeToLocalDisplay(value),
      },
      {
        title: 'File Type',
        dataIndex: 'fileType',
        key: 'fileType',
      },
      {
        title: 'Organisation',
        dataIndex: 'companyName',
        key: 'companyName',
        render: (value) => value || '-',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_, row) => (
          <Button
            type="link"
            className="px-0"
            onClick={() =>
              navigate(
                row.reportBuilderId && row.reportBuilderId !== '00000000-0000-0000-0000-000000000000'
                  ? `/reports/custom/${row.reportBuilderId}/${row.reportBuilderScheduleId}`
                  : `/reports/custom/${row.reportBuilderGroupId}/${row.reportBuilderScheduleId}?type=group`
              )
            }
          >
            Edit
          </Button>
        ),
      },
    ],
    [navigate]
  )

  return (
    <Card>
      <Space direction="vertical" size={16} className="w-full">
        <Space className="w-full justify-between" align="start">
          <div>
            <Typography.Title level={3} className="!mb-1">
              {isGroup ? 'Group Schedules' : 'Report Schedules'}
            </Typography.Title>
            <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
          </div>
          <Space>
            <Button onClick={() => navigate('/reports/created')}>Back to Reports</Button>
            {!isGroup ? (
              <Button type="primary" onClick={() => navigate(`/reports/custom/${reportBuilderId}`)}>
                Open Report
              </Button>
            ) : null}
          </Space>
        </Space>

        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search schedules"
          suffix={<SearchOutlined />}
          allowClear
          style={{ width: 320 }}
          aria-label="Search report schedules"
        />

        {schedulesQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="Failed to load schedules"
            description={schedulesQuery.error instanceof Error ? schedulesQuery.error.message : 'Unknown error'}
            action={<Button onClick={() => schedulesQuery.refetch()}>Retry</Button>}
          />
        ) : null}

        {!schedulesQuery.isError && !schedulesQuery.isLoading && filteredRows.length === 0 ? (
          <Empty description="No schedules found for this report" />
        ) : null}

        <Table<ReportBuilderScheduleListItem>
          rowKey="reportBuilderScheduleId"
          loading={schedulesQuery.isLoading}
          columns={columns}
          dataSource={filteredRows}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        />
      </Space>
    </Card>
  )
}
